"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useCompetitionSlice } from "@/views/competition-shared/CompetitionSliceProvider";
import { StatsTable } from "fansunited-sports-ui";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import type { ResolvedPromoRow } from "@/features/promo-slots/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { SITE_TYPE_FOOTBALL_TEAM } from "@/config/siteTypes";
import { mapStandingsToTable, DEFAULT_COLUMNS } from "@/utilities/mappers/standings";
import { GroupMatches } from "./GroupMatches";
import type { FUSportsStandingsWithNativeTeamIds, StandingsGroup } from "@/utilities/data/types";
import type { MatchRow } from "@/utilities/data/competition/derive/types";
const tableStyles = {
  borderRadius: "md" as const,
  shadow: "md" as const,
  typography: {
    cell: "text-sm font-medium text-foreground",
    header: "text-xs uppercase font-bold text-muted-foreground"
  }
};
interface InternationalStandingsProps {
  hideHeader?: boolean;
  /** Primary standings; undefined when the competition has none. */
  standings: FUSportsStandingsWithNativeTeamIds | undefined;
  /** Standings bucketed by group, `name` is the group letter. */
  groupedStandings: StandingsGroup[];
  /** Per-group match lists keyed by group letter. */
  groupMatches: Record<string, MatchRow[]>;
  /** Resolved header colors from section styles */
  headerColors?: ResolvedHeaderColors;
  /** Optional breadcrumbs element rendered inside the BrandedHeader (comp-site path). */
  breadcrumbs?: React.ReactNode;
  /** Resolved promos for the standings page. */
  promos?: (ResolvedPromoRow | null)[];
}

/**
 * International competition standings — full page.
 * Renders all groups stacked vertically using the existing mapStandingsToTable mapper.
 */
export function InternationalStandings({
  hideHeader = false,
  standings,
  groupedStandings,
  groupMatches,
  headerColors,
  breadcrumbs,
  promos
}: InternationalStandingsProps) {
  const {
    competition,
    seasonName
  } = useCompetitionSlice();
  const labels = useLabels();
  const nav = useNavigation();
  const {
    siteType
  } = useTenantSettings();
  const teamHref = siteType === SITE_TYPE_FOOTBALL_TEAM ? undefined : nav.team;
  const competitionHref = useMemo(() => {
    if (!competition?.id) return undefined;
    try {
      return nav.footballCompetition(competition.id);
    } catch {
      return undefined;
    }
  }, [competition?.id, nav]);
  if (!standings) {
    return <div className="min-h-screen">
                {!hideHeader && <BrandedHeader title={labels.standings.title} subtitle={`${competition?.name || ""} ${seasonName || ""}`} imageUrl={competition?.assets?.logo} image={<TeamLogo src={competition?.assets?.logo} kind="competition" size="xl" />} href={competitionHref} colors={headerColors?.colors} backgroundImageUrl={headerColors?.backgroundImageUrl} breadcrumbs={breadcrumbs} />}
                <div className="container mx-auto px-4 py-8">
                    <div className="text-center py-12 text-muted-foreground">
                        <p className="text-lg font-semibold mb-2">{labels.standings.noStandings}</p>
                        <p className="text-sm">{labels.standings.noStandingsDescription}</p>
                    </div>
                </div>
            </div>;
  }
  return <>
            {!hideHeader && <BrandedHeader title={labels.standings.title} subtitle={`${competition?.name ?? ""} ${seasonName ?? ""}`.trim()} imageUrl={competition?.assets?.logo ?? undefined} image={<TeamLogo src={competition?.assets?.logo} kind="competition" size="xl" />} href={competitionHref} colors={headerColors?.colors} backgroundImageUrl={headerColors?.backgroundImageUrl} breadcrumbs={breadcrumbs} />}

            <div className="max-w-350 w-full mx-auto px-4 py-8 space-y-8">
                <div className="space-y-8">
                    {groupedStandings.map(gs => <GroupTable key={gs.name} groupedStandings={gs} teamHref={teamHref}>
                            <GroupMatches matches={groupMatches[gs.name ?? ""] ?? []} />
                        </GroupTable>)}
                </div>
                <PromoSlot row={promos?.[0] ?? null} />
            </div>
        </>;
}
function GroupTable(t0) {
  const $ = _c(16);
  const {
    groupedStandings,
    teamHref,
    children
  } = t0;
  const labels = useLabels();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      legend: []
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== groupedStandings.entries) {
    t2 = {
      entries: groupedStandings.entries,
      metadata: t1
    };
    $[1] = groupedStandings.entries;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const pseudoStandings = t2;
  const t3 = pseudoStandings as any;
  let t4;
  if ($[3] !== labels.standings || $[4] !== t3 || $[5] !== teamHref) {
    t4 = mapStandingsToTable(t3, labels.standings, DEFAULT_COLUMNS.filter(_temp), undefined, teamHref);
    $[3] = labels.standings;
    $[4] = t3;
    $[5] = teamHref;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const mapped = t4;
  const t5 = `${labels.standings.table.group} ${groupedStandings.name}`;
  let t6;
  if ($[7] !== t5) {
    t6 = <h3 className="text-lg font-bold mb-3">{t5}</h3>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== mapped.columns || $[10] !== mapped.rows) {
    t7 = <StatsTable columns={mapped.columns} rows={mapped.rows} styles={tableStyles} />;
    $[9] = mapped.columns;
    $[10] = mapped.rows;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== children || $[13] !== t6 || $[14] !== t7) {
    t8 = <div>{t6}{t7}{children}</div>;
    $[12] = children;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function _temp(c) {
  return c !== "form";
}